import {
  useCurrentDate,
  Button,
  Icon,
  ProfilePicture,
  setFallbackImage,
  type IconType,
} from "@sellar-io/components";
import type { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { DiscoveryHorizontalList } from "../components/DiscoveryHorizontalList";
import { DiscoveryV2DistributionSupplierCard } from "./DiscoveryV2DistributionSupplierCard";
import { TrackedCutoffCountdown } from "./CutoffCountdown/TrackedCutoffCountdown";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/shallow";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { useDiscoveryDistributionSupplierList } from "../../../../hooks/api/v2/discovery/useDiscoveryDistributionSupplierList";
import {
  DISCOVER_DISTRIBUTION,
  DISCOVER_SEARCH,
  DRAFT_ORDERS,
  UNIFIED_CHECKOUT_BASKET,
} from "../../../../constants/routes";
import { useDiscoveryV2SearchStore } from "../DiscoveryV2SearchStore";
import { useDiscoveryV2ProductList } from "~/hooks/api/v2/discovery/useDiscoveryV2ProductList";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { useDraftDbsBaskets } from "./useDraftDbsBaskets";
import { pluralize } from "@sellar-io/common";

dayjs.extend(isoWeek);

const now = new Date();
const fridayOfCurrentWeek = dayjs(now).isoWeekday(5).endOf("day").toDate();

type SupplierListVisibilityState =
  | "hidden"
  | "visible"
  | "visible-when-inactive";

type DiscoveryV2DistributionSuppliersBannerV2ContainerProps = {
  supplierListVisibility: SupplierListVisibilityState;
  unAuthorisedUserPostcode?: string;
  contentClassName?: string;
  className?: string;
  onTrackSupplierClick: (params: { supplierName: string }) => void;
  onTrackBannerClick: () => void;
  onOpenDbsDraftBasketDrawer: () => void;
};

export const DiscoveryV2DistributionSuppliersBannerV2Container = ({
  supplierListVisibility,
  unAuthorisedUserPostcode,
  contentClassName,
  className,
  onTrackSupplierClick,
  onTrackBannerClick,
  onOpenDbsDraftBasketDrawer,
}: DiscoveryV2DistributionSuppliersBannerV2ContainerProps) => {
  const currentDate = useCurrentDate();
  const navigate = useNavigate();
  const { business } = useSellarCurrentBusiness();
  const { baskets, dbsDraftOrders, storefrontDraftOrders } =
    useDraftDbsBaskets();
  const buyerPostcode =
    business?.DeliveryAddress.postalCode || unAuthorisedUserPostcode || "";

  const {
    setSearchValue,
    setIsDeliveredBySellar,
    setDeliveredBySellarOption,
    setSelectedSuppliers,
    reset,
  } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      setSearchValue: state.setSearchValue,
      setIsDeliveredBySellar: state.setIsDeliveredBySellar,
      setDeliveredBySellarOption: state.setDeliveredBySellarOption,
      setSelectedSuppliers: state.setSelectedSuppliers,
      reset: state.reset,
    })),
  );

  const supplierListQuery = useDiscoveryDistributionSupplierList(
    {
      query: [
        {
          type: "retailerPostalCode",
          modifier: "EQUALS",
          value: buyerPostcode,
        },
      ],
      limit: 20,
    },
    { enabled: Boolean(buyerPostcode) },
  );

  const suppliers = supplierListQuery.data?.pages.flat() ?? [];

  const sdProductsArrivingThisWeekendQuery = useDiscoveryV2ProductList({
    limit: 1,
    retailerPostcode: buyerPostcode,
    query: [
      {
        type: "arrivingByDate",
        modifier: "BETWEEN",
        value: {
          low: now,
          high: fridayOfCurrentWeek,
        },
      },
      {
        type: "deliveredBySellar",
        modifier: "EQUALS",
        value: true,
      },
    ],
  });

  const productsArrivingThisWeekend =
    sdProductsArrivingThisWeekendQuery.data?.pages.flat() ?? [];

  const hasProductsArrivingThisWeekend = productsArrivingThisWeekend.length > 0;

  const handleNavigate = () => {
    onTrackBannerClick();
    navigate(DISCOVER_DISTRIBUTION);
  };

  const handleSupplierClick = (supplierId: number) => {
    const supplier = suppliers.find((entry) => entry.id === supplierId);
    if (!supplier) return;

    const supplierName = supplier.Business.name;
    onTrackSupplierClick({ supplierName });
    reset();
    setSearchValue("");
    setIsDeliveredBySellar(true);
    setDeliveredBySellarOption("all");
    setSelectedSuppliers([{ id: supplier.id, name: supplierName }]);
    navigate(DISCOVER_SEARCH);
  };

  const handleGetItByTheWeekend = () => {
    setSearchValue("");
    setIsDeliveredBySellar(true);
    setDeliveredBySellarOption("weekend");
    navigate(DISCOVER_SEARCH);
  };

  const handleShopAll = () => {
    setSearchValue("");
    setIsDeliveredBySellar(true);
    setDeliveredBySellarOption("all");
    navigate(DISCOVER_SEARCH);
  };

  const handleCheckout = async () => {
    const sdBasketId = primaryDraftBasket?.uuid;

    if (!sdBasketId) {
      console.error("No primary draft basket found for checkout");
      return;
    }

    navigate(UNIFIED_CHECKOUT_BASKET.href({ sdBasketUuid: sdBasketId }));
  };

  if (!buyerPostcode || suppliers.length === 0) return null;

  // This is this week's basket or next week's basket, which is the primary basket for the banner. If there are multiple baskets for this week, we will show the first one.
  const primaryDraftBasket =
    baskets.find((basket) => basket.window === "weekend") ||
    baskets.find((basket) => basket.window === "nextWeek");

  const itemCount =
    primaryDraftBasket?.DraftOrders.flatMap((order) => order.Items).reduce(
      (acc, item) => acc + item.quantity,
      0,
    ) || 0;

  const primaryDeliveryDate =
    primaryDraftBasket?.requestedDeliveryAt ?? currentDate;

  const productMap = new Map(
    dbsDraftOrders
      .flatMap((order) => order.OrderItems)
      .map((item) => [item?.ProductId ?? 0, item?.Product]),
  );

  const primaryBasketProducts =
    primaryDraftBasket?.DraftOrders.flatMap((order) =>
      order.Items.map((item) => {
        const product = productMap.get(item.ProductId);
        const [parentName, variantName] = (product?.name ?? "")
          .split("•")
          .map((s) => s.trim());

        const heroImageUrl = setFallbackImage({
          img: product?.Parent?.imageUrl ?? product?.imageUrl ?? null,
          fallback: "product-hero",
        });

        return {
          id: item.id,
          name: parentName || "",
          description: variantName || "",
          imageUrl: heroImageUrl || "",
          supplierLogoUrl: order.Supplier.Business.logoUrl || "",
        };
      }),
    ) ?? [];

  const storefrontDraftOrdersItemCount = storefrontDraftOrders
    .flatMap((order) => order.OrderItems)
    .reduce((acc, item) => acc + (item?.quantity || 0), 0);

  const secondaryDraftBaskets: DistributionSecondaryBasketData | null =
    storefrontDraftOrders.length > 0
      ? {
          variant: "draft-orders",
          basketCount: storefrontDraftOrders.length,
          itemCount: storefrontDraftOrdersItemCount,
        }
      : null;

  const nextWeekDraftBaskets = baskets.filter(
    (basket) =>
      basket.window === "nextWeek" && basket.uuid !== primaryDraftBasket?.uuid,
  );

  const nextWeekItemCount = nextWeekDraftBaskets
    .flatMap((basket) => basket.DraftOrders)
    .reduce(
      (acc, order) =>
        acc + order.Items.reduce((itemAcc, item) => itemAcc + item.quantity, 0),
      0,
    );

  const secondaryNextWeekBasket: DistributionSecondaryBasketData | null =
    nextWeekDraftBaskets.length > 0
      ? {
          variant: "next-week",
          basketCount: nextWeekDraftBaskets.length,
          itemCount: nextWeekItemCount,
        }
      : null;

  const secondaryBaskets = [
    secondaryDraftBaskets,
    secondaryNextWeekBasket,
  ].filter(Boolean);

  const bannerState = getDistributionBannerState({
    hasPrimaryBasket: Boolean(primaryDraftBasket),
  });

  const showSuppliers = computeIsSupplierListVisible({
    supplierListVisibility,
    bannerState,
  });

  return (
    <>
      <div
        className={twMerge(
          "flex w-full flex-col items-center bg-[#E8D3F433]",
          className,
        )}
      >
        <div
          className={twMerge(
            "flex w-full max-w-300 min-w-0 flex-col rounded-md",
            contentClassName,
          )}
        >
          <DiscoveryV2DistributionSuppliersBannerV2
            showSuppliers={showSuppliers}
            orderCutoffAt={primaryDraftBasket?.orderCutoffAt ?? null}
            state={bannerState}
            deliveryDate={primaryDeliveryDate}
            products={primaryBasketProducts}
            secondaryBaskets={secondaryBaskets}
            itemCount={itemCount}
            suppliers={suppliers.map((supplier) => ({
              id: supplier.id,
              name: supplier.Business.name,
              location: supplier.Business.DeliveryAddress.city,
              imageUrl: supplier.imageUrl ?? "",
              logoUrl: supplier.Business.logoUrl ?? "",
            }))}
            primaryAction={
              hasProductsArrivingThisWeekend
                ? {
                    variant: "get-it-by-the-weekend",
                    onClick: handleGetItByTheWeekend,
                  }
                : { variant: "shop-all", onClick: handleShopAll }
            }
            onViewMoreSuppliers={handleNavigate}
            onLearnMore={handleNavigate}
            onSupplierClick={handleSupplierClick}
            onViewBasket={onOpenDbsDraftBasketDrawer}
            onCheckout={handleCheckout}
            onContinueShopping={handleShopAll}
            onViewSecondaryBasket={(basketVariant) => {
              if (basketVariant === "next-week") {
                onOpenDbsDraftBasketDrawer();
                return;
              }

              if (basketVariant === "draft-orders") {
                navigate(DRAFT_ORDERS);
                return;
              }
            }}
          />
        </div>
      </div>
    </>
  );
};

type DistributionCutoff =
  | { variant: "none" }
  | { variant: "today" | "soon"; orderCutoffAt: Date };

const cutoffIcons: Record<DistributionCutoff["variant"], IconType> = {
  none: "TIMER_SAND_EMPTY",
  today: "CLOCK_OUTLINE",
  soon: "INFORMATION",
};

type DistributionBasketProduct = {
  id: number;
  name: string;
  description: string;
  imageUrl?: string;
  supplierLogoUrl?: string;
};

type DistributionSupplier = {
  id: number;
  name: string;
  location: string;
  imageUrl?: string;
  logoUrl?: string;
};

type DistributionSecondaryBasketData = {
  variant: "next-week" | "draft-orders";
  basketCount: number;
  itemCount: number;
};

type DistributionBannerState = "inactive" | "active";

const getDistributionBannerState = ({
  hasPrimaryBasket,
}: {
  hasPrimaryBasket: boolean;
}): DistributionBannerState => {
  if (hasPrimaryBasket) return "active";
  return "inactive";
};

type DistributionInactivePrimaryAction = {
  variant: "get-it-by-the-weekend" | "shop-all";
  onClick: () => void;
};

const primaryActionLabels = {
  "get-it-by-the-weekend": "Get it by the weekend",
  "shop-all": "Shop all",
  "review-checkout": "Review & checkout",
};

type DiscoveryV2DistributionSuppliersBannerV2Props = {
  showSuppliers: boolean;
  suppliers: DistributionSupplier[];
  primaryAction: DistributionInactivePrimaryAction;
  itemCount: number;
  deliveryDate: Date;
  state: DistributionBannerState;
  orderCutoffAt: Date | null;
  products: DistributionBasketProduct[];
  secondaryBaskets: DistributionSecondaryBasketData[];
  className?: string;
  onLearnMore: () => void;
  onViewBasket: () => void;
  onCheckout: () => void;
  onContinueShopping: () => void;
  onViewSecondaryBasket: (
    basketVariant: DistributionSecondaryBasketData["variant"],
  ) => void;
  onSupplierClick: (supplierId: number) => void;
  onViewMoreSuppliers: () => void;
};

const DiscoveryV2DistributionSuppliersBannerV2 = ({
  showSuppliers,
  state,
  orderCutoffAt = null,
  deliveryDate: providedDeliveryDate,
  products = [],
  itemCount = 0,
  suppliers,
  secondaryBaskets = [],
  className,
  primaryAction,
  onLearnMore,
  onViewBasket,
  onCheckout,
  onContinueShopping,
  onViewSecondaryBasket,
  onSupplierClick,
  onViewMoreSuppliers,
}: DiscoveryV2DistributionSuppliersBannerV2Props) => {
  const currentDate = useCurrentDate();
  const deliveryDate = providedDeliveryDate ?? currentDate;
  return (
    <section
      aria-label="Delivered by Sellar"
      className={twMerge(
        "@container/banner flex w-full min-w-0 flex-col gap-4 text-zinc-800",
        className,
      )}
    >
      <div className="flex flex-col gap-3 rounded-2xl border border-zinc-200 bg-white p-3 shadow-sm">
        {state === "active" ? (
          <button
            type="button"
            onClick={onViewBasket}
            className={twMerge(
              "cursor-pointer rounded-xl border border-[#E8D3F4]/60 bg-[#F8F2FC] px-3 py-3 text-left hover:bg-[#F8F2FC]/70 @min-[768px]/banner:px-4",
              focusRing,
            )}
          >
            <DistributionDeliveryHeader
              deliveryDate={deliveryDate}
              itemCount={itemCount}
              orderCutoffAt={orderCutoffAt}
            />
            <DistributionBasketPreview products={products} />
          </button>
        ) : (
          <div className="rounded-xl">
            <DistributionInactiveHeader />
          </div>
        )}
        {state === "active" && secondaryBaskets.length > 0 && (
          <div className="flex flex-col gap-2">
            {secondaryBaskets.map((basket) => (
              <DistributionSecondaryBasket
                key={basket.variant}
                basket={basket}
                onClick={() => onViewSecondaryBasket?.(basket.variant)}
              />
            ))}
          </div>
        )}
        <DistributionBannerActions
          state={state}
          primaryAction={
            state === "active"
              ? { variant: "review-checkout", onClick: onCheckout }
              : primaryAction
          }
          onSecondaryClick={
            state === "inactive" ? onLearnMore : onContinueShopping
          }
        />
      </div>
      {showSuppliers && (
        <DistributionSupplierList
          suppliers={suppliers}
          onSupplierClick={onSupplierClick}
          onViewMore={onViewMoreSuppliers}
        />
      )}
    </section>
  );
};

const computeIsSupplierListVisible = ({
  supplierListVisibility,
  bannerState,
}: {
  supplierListVisibility: SupplierListVisibilityState;
  bannerState: DistributionBannerState;
}): boolean => {
  switch (supplierListVisibility) {
    case "hidden":
      return false;
    case "visible":
      return true;
    case "visible-when-inactive":
      return bannerState === "inactive";
    default:
      return false;
  }
};

const focusRing =
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8C23C7]";

const DistributionRoundIcon = ({ icon }: { icon: IconType }) => (
  <span
    aria-hidden="true"
    className="flex size-14 shrink-0 items-center justify-center rounded-full bg-[#E8D3F4]/30 text-[#A34FD2]"
  >
    <span className="flex size-10 items-center justify-center rounded-full bg-[#E8D3F4]/70">
      <Icon icon={icon} size={1} />
    </span>
  </span>
);

const DistributionBenefit = ({ children }: { children: ReactNode }) => (
  <li className="flex items-center gap-1">
    <span
      aria-hidden="true"
      className="inline-flex shrink-0 items-center justify-center leading-none"
    >
      <Icon
        icon="CHECKBOX_MARKED_CIRCLE"
        size={0.5}
        className="text-[#BA7BDD]"
      />
    </span>
    <span>{children}</span>
  </li>
);

const DistributionInactiveHeader = () => (
  <div className="flex items-center gap-3 rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3">
    <div className="hidden @min-[768px]/banner:block">
      <DistributionRoundIcon icon="SELLAR_DISTRIBUTION_TRUCK" />
    </div>
    <div className="min-w-0">
      <h2 className="text-base font-semibold">Delivered by Sellar</h2>
      <ul className="mt-1 flex flex-col gap-x-2 gap-y-1 text-sm text-zinc-500 @min-[768px]/banner:mt-0 @min-[768px]/banner:flex-row @min-[768px]/banner:flex-wrap [&_strong]:font-semibold [&_strong]:text-[#A34FD2]">
        <DistributionBenefit>
          <strong>Expert delivery</strong> - no couriers or pallets
        </DistributionBenefit>
        <DistributionBenefit>
          <strong>Free</strong> with <strong>no minimums</strong>
        </DistributionBenefit>
        <DistributionBenefit>
          Multiple suppliers <strong>delivered together</strong>
        </DistributionBenefit>
      </ul>
    </div>
  </div>
);

export const DistributionDeliveryHeader = ({
  deliveryDate,
  itemCount,
  orderCutoffAt = null,
}: {
  deliveryDate: Date;
  itemCount: number;
  orderCutoffAt?: Date | null;
}) => {
  const cutoff = getDistributionCutOff({ orderCutoffAt });

  return (
    <div className="flex flex-col items-start gap-2 @min-[768px]/banner:flex-row @min-[768px]/banner:items-center @min-[768px]/banner:justify-between @min-[768px]/banner:gap-4">
      <div className="flex min-w-0 items-center gap-3">
        <div className="hidden @min-[768px]/banner:block">
          <DistributionRoundIcon icon="CART_OUTLINE" />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-semibold @min-[768px]/banner:text-base">
              Planned delivery{" "}
              <span className="text-[#A34FD2]">
                {dayjs(deliveryDate).format("dddd D MMM")}
              </span>
            </h2>
            {cutoff.variant !== "none" && (
              <span
                className={twMerge(
                  "rounded px-1 py-0.5 text-[10px] leading-tight font-semibold uppercase",
                  cutoff.variant === "soon"
                    ? "bg-[#A34FD2] text-white"
                    : "bg-[#E8D3F4]/60 text-[#A34FD2]",
                )}
              >
                Cut-off {cutoff.variant}
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-500">
            {itemCount} {pluralize("item", itemCount)} •{" "}
            <strong className="font-semibold">Free delivery</strong> by Sellar
          </p>
        </div>
      </div>
      <span
        className={twMerge(
          "order-first inline-flex shrink-0 items-center justify-center gap-1 rounded-full px-2 py-1 text-sm leading-none font-semibold @min-[768px]/banner:order-0",
          cutoff.variant === "soon"
            ? "bg-[#A34FD2] text-white"
            : "bg-[#E8D3F4]/40 text-[#A34FD2]",
        )}
      >
        {cutoff.variant === "none" ? (
          <DistributionBasketInProgress />
        ) : (
          <>
            <span
              aria-hidden="true"
              className="inline-flex shrink-0 items-center justify-center leading-none"
            >
              <Icon icon={cutoffIcons[cutoff.variant]} size={0.6} />
            </span>
            <TrackedCutoffCountdown cutoff={cutoff.orderCutoffAt} />
          </>
        )}
      </span>
    </div>
  );
};

const DistributionBasketInProgress = () => (
  <div className="flex items-center gap-0">
    <span
      aria-hidden="true"
      className="inline-flex shrink-0 items-center justify-center px-2 py-1 leading-none"
    >
      <Icon icon="TIMER_SAND_EMPTY" size={0.6} />
    </span>
    <span>Basket in progress</span>
  </div>
);

const DistributionTextAction = ({
  children,
  compactOnMobile = false,
}: {
  children: ReactNode;
  compactOnMobile?: boolean;
}) => (
  <span
    className={twMerge(
      "inline-flex min-h-11 shrink-0 cursor-pointer items-center justify-center gap-1 rounded py-2 text-sm font-semibold text-[#A34FD2] hover:text-[#8C23C7] @min-[768px]/banner:min-h-0",
      compactOnMobile && "min-w-11 @min-[768px]/banner:min-w-0",
      focusRing,
    )}
  >
    <span
      className={
        compactOnMobile ? "sr-only @min-[768px]/banner:not-sr-only" : undefined
      }
    >
      {children}
    </span>
    <span
      aria-hidden="true"
      className="inline-flex shrink-0 items-center justify-center leading-none"
    >
      <Icon icon="CHEVRON_RIGHT" size={0.65} />
    </span>
  </span>
);

const DistributionBasketProductPreview = ({
  product,
}: {
  product: DistributionBasketProduct;
}) => (
  <li className="flex min-w-0 items-center gap-2 @min-[768px]/banner:pr-4 @min-[768px]/banner:not-last:border-r @min-[768px]/banner:not-last:border-[#E8D3F4]/50">
    <div className="relative size-11 shrink-0">
      <ProfilePicture
        logoUrl={product.imageUrl}
        name={product.name}
        fallback="product-badge"
        className="h-11 max-h-11 min-h-11 w-11 max-w-11 min-w-11 rounded-md"
      />
      {product.supplierLogoUrl && (
        <img
          src={product.supplierLogoUrl}
          alt=""
          className="absolute -right-1 -bottom-1 size-4 rounded-full border border-white bg-white object-cover"
        />
      )}
    </div>
    <div className="sr-only @min-[768px]/banner:not-sr-only @min-[768px]/banner:w-40 @min-[768px]/banner:max-w-40 @min-[768px]/banner:min-w-0">
      <p className="truncate text-sm font-semibold">{product.name}</p>
      <p className="truncate text-sm text-zinc-500">{product.description}</p>
    </div>
  </li>
);

const DistributionBasketPreview = ({
  products,
}: {
  products: readonly DistributionBasketProduct[];
}) => {
  if (products.length === 0) return null;

  return (
    <div className="mt-3 flex items-center justify-between gap-1 border-t border-[#E8D3F4]/50 pt-3 @min-[768px]/banner:gap-4">
      <ul
        aria-label="Products in your basket"
        className="flex min-w-0 flex-wrap items-center gap-2 @min-[768px]/banner:flex-nowrap @min-[768px]/banner:gap-3"
      >
        {products.slice(0, 3).map((product) => (
          <DistributionBasketProductPreview
            key={product.id}
            product={product}
          />
        ))}
        {products.length > 3 && (
          <li
            aria-label={`${products.length - 3} more products`}
            className="flex size-10 shrink-0 items-center justify-center rounded-md bg-[#E8D3F4]/40 text-sm font-semibold text-[#A34FD2]"
          >
            +{products.length - 3}
          </li>
        )}
      </ul>
      <DistributionTextAction compactOnMobile>
        View basket
      </DistributionTextAction>
    </div>
  );
};

const DistributionSecondaryBasket = ({
  basket,
  onClick,
}: {
  basket: DistributionSecondaryBasketData;
  onClick: () => void;
}) => {
  const nextWeek = basket.variant === "next-week";
  return (
    <button
      type="button"
      onClick={onClick}
      className={twMerge(
        "flex w-full cursor-pointer items-center justify-between gap-1 rounded-xl border border-zinc-200 bg-white py-1 pr-1 pl-3 text-left hover:bg-gray-50 @min-[768px]/banner:gap-4 @min-[768px]/banner:pr-3",
        focusRing,
      )}
    >
      <p className="flex items-center gap-1 text-sm text-zinc-500">
        <span
          aria-hidden="true"
          className="inline-flex shrink-0 items-center justify-center leading-none"
        >
          <Icon
            icon={nextWeek ? "CART_OUTLINE" : "PROGRESS_HELPER"}
            size={0.65}
            className={nextWeek ? "text-[#A34FD2]" : "text-zinc-400"}
          />
        </span>
        <span>
          <strong
            aria-hidden="true"
            className="font-semibold text-zinc-700 @min-[768px]/banner:hidden"
          >
            {basket.basketCount}{" "}
            {nextWeek
              ? `more ${pluralize("basket", basket.basketCount)}`
              : `draft ${pluralize("order", basket.basketCount)}`}
          </strong>
          <strong className="sr-only font-semibold text-zinc-700 @min-[768px]/banner:not-sr-only">
            {basket.basketCount}{" "}
            {nextWeek
              ? `more ${pluralize("basket", basket.basketCount)} ready for next week`
              : `draft ${pluralize("order", basket.basketCount)} with other suppliers`}
          </strong>{" "}
          • {basket.itemCount} {pluralize("item", basket.itemCount)}
        </span>
      </p>
      <DistributionTextAction compactOnMobile>
        {nextWeek ? "View basket" : "View draft orders"}
      </DistributionTextAction>
    </button>
  );
};

const DistributionBannerActions = ({
  state,
  primaryAction,
  onSecondaryClick,
}: {
  state: DistributionBannerState;
  primaryAction:
    | DistributionInactivePrimaryAction
    | { variant: "review-checkout"; onClick: () => void };
  onSecondaryClick: () => void;
}) => (
  <div className="flex flex-col gap-2 @min-[768px]/banner:flex-row">
    <Button
      onClick={primaryAction.onClick}
      className={twMerge(
        "w-full flex-1 bg-[#8C23C7] hover:bg-[#7620A7]",
        focusRing,
      )}
    >
      {primaryActionLabels[primaryAction.variant]}
    </Button>
    <Button
      onClick={onSecondaryClick}
      color="secondary"
      className={twMerge("w-full flex-1", focusRing)}
    >
      {state === "inactive" ? "Learn more" : "Continue shopping"}
    </Button>
  </div>
);

const DistributionSupplierList = ({
  suppliers,
  onSupplierClick,
  onViewMore,
}: {
  suppliers: readonly DistributionSupplier[];
  onSupplierClick: (supplierId: number) => void;
  onViewMore: () => void;
}) => {
  if (suppliers.length === 0) return null;
  return (
    <DiscoveryHorizontalList
      title="Shop by supplier"
      hideViewMore={false}
      containerClassName="min-w-0 px-0 pt-0"
      onClick={onViewMore}
    >
      <ul aria-label="Shop by supplier" className="flex gap-3">
        {suppliers.map((supplier) => (
          <li key={supplier.id} className="shrink-0">
            <DiscoveryV2DistributionSupplierCard
              imageUrl={supplier.imageUrl ?? ""}
              logoUrl={supplier.logoUrl ?? ""}
              businessName={supplier.name}
              deliveryAddressCity={supplier.location}
              onClick={() => onSupplierClick(supplier.id)}
              className={focusRing}
            />
          </li>
        ))}
      </ul>
    </DiscoveryHorizontalList>
  );
};

const getDistributionCutOff = ({
  orderCutoffAt,
}: {
  orderCutoffAt: Date | null;
}): DistributionCutoff => {
  if (!orderCutoffAt) return { variant: "none" };

  const now = dayjs();
  const cutoffTime = dayjs(orderCutoffAt);
  if (!cutoffTime.isValid()) return { variant: "none" };

  const hoursUntilCutoff = cutoffTime.diff(now, "hour", true);
  if (hoursUntilCutoff > 0 && hoursUntilCutoff <= 1) {
    return { variant: "soon", orderCutoffAt };
  }

  if (cutoffTime.isSame(now, "day")) {
    return { variant: "today", orderCutoffAt };
  }

  return { variant: "none" };
};
